// Hook : charge le layout dashboard pour un hub + catalogue des widgets
// disponibles, et expose les helpers pour le modifier/persister.
//
// Pattern : optimistic update sur le state local, save() persiste en DB.
// La cascade (user > role > tenant > shipped) est gérée côté backend.
import { useCallback, useEffect, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

function authHeaders() {
  const token = localStorage.getItem("session_token");
  return {
    "Content-Type": "application/json",
    "X-Tenant-ID": "tef",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export function useDashboardLayout(hub, { role = null, userEmail = null } = {}) {
  const [layout, setLayout] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (role) params.set("role", role);
      if (userEmail) params.set("user_email", userEmail);
      const qs = params.toString() ? `?${params}` : "";
      const [layoutRes, catalogRes] = await Promise.all([
        fetch(`${API}/api/dashboard/${hub}/layout${qs}`, { headers: authHeaders() }),
        fetch(`${API}/api/dashboard/${hub}/widgets${role ? `?role=${encodeURIComponent(role)}` : ""}`, {
          headers: authHeaders(),
        }),
      ]);
      if (!layoutRes.ok) throw new Error(`layout HTTP ${layoutRes.status}`);
      if (!catalogRes.ok) throw new Error(`catalog HTTP ${catalogRes.status}`);
      setLayout(await layoutRes.json());
      const cat = await catalogRes.json();
      setCatalog(cat.items || []);
    } catch (e) {
      setError(String(e));
    } finally {
      setLoading(false);
    }
  }, [hub, role, userEmail]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (next) => {
    setSaving(true);
    try {
      const body = { ...(layout || {}), ...next, hub };
      const res = await fetch(`${API}/api/dashboard/${hub}/layout`, {
        method: "PUT",
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(`PUT HTTP ${res.status}`);
      const saved = await res.json();
      setLayout(saved);
      return saved;
    } catch (e) {
      setError(String(e));
      throw e;
    } finally {
      setSaving(false);
    }
  }, [hub, layout]);

  const reset = useCallback(async () => {
    setSaving(true);
    try {
      const params = new URLSearchParams();
      if (role) params.set("role", role);
      if (userEmail) params.set("user_email", userEmail);
      const qs = params.toString() ? `?${params}` : "";
      await fetch(`${API}/api/dashboard/${hub}/reset${qs}`, {
        method: "POST", headers: authHeaders(),
      });
      await load();
    } finally {
      setSaving(false);
    }
  }, [hub, role, userEmail, load]);

  // Helpers locaux — modifient le layout en mémoire (à confirmer via save())
  const toggleWidget = useCallback((widgetId) => {
    setLayout((prev) => {
      if (!prev) return prev;
      const widgets = prev.widgets || [];
      const existing = widgets.find((w) => w.id === widgetId);
      if (existing) {
        return {
          ...prev,
          widgets: widgets.map((w) =>
            w.id === widgetId ? { ...w, enabled: !w.enabled } : w
          ),
        };
      }
      // Pas encore dans le layout — l'ajouter activé en fin de liste
      const meta = catalog.find((c) => c.id === widgetId);
      const next = {
        id: widgetId,
        enabled: true,
        order: widgets.length,
        size: meta?.default_size || "md",
        config: {},
      };
      return { ...prev, widgets: [...widgets, next] };
    });
  }, [catalog]);

  const moveWidget = useCallback((widgetId, direction) => {
    setLayout((prev) => {
      if (!prev) return prev;
      const sorted = [...(prev.widgets || [])].sort(
        (a, b) => (a.order || 0) - (b.order || 0)
      );
      const idx = sorted.findIndex((w) => w.id === widgetId);
      const target = direction === "up" ? idx - 1 : idx + 1;
      if (idx < 0 || target < 0 || target >= sorted.length) return prev;
      const swapped = [...sorted];
      [swapped[idx], swapped[target]] = [swapped[target], swapped[idx]];
      return {
        ...prev,
        widgets: swapped.map((w, i) => ({ ...w, order: i })),
      };
    });
  }, []);

  const resizeWidget = useCallback((widgetId, size) => {
    setLayout((prev) =>
      prev ? {
        ...prev,
        widgets: (prev.widgets || []).map((w) =>
          w.id === widgetId ? { ...w, size } : w
        ),
      } : prev
    );
  }, []);

  return {
    layout, catalog, loading, saving, error,
    load, save, reset,
    toggleWidget, moveWidget, resizeWidget,
  };
}
